import { useState } from 'react';
import { toast } from 'sonner';
import { errorText, useTechActions, type CantReason, type TechTask } from '../api';
import { CANT_REASONS, sendsTo, shortName } from '../format';
import { deviceLine } from './DeviceCard';
import { NoteField } from './NoteField';
import { TechModal } from './TechModal';

/** "Can’t — OCT · macula · Nabil R." — each caller names its own test. */
export function cantTitle(t: TechTask): string {
  return `Can’t — ${t.title.replace(/\s+—\s+/g, ' · ')}${t.patient ? ` · ${shortName(t.patient)}` : ''}`;
}

/**
 * CAN'T — the reason, and where it goes (g_cantb). Five agnostic reasons and
 * "Other — write the reason"; the note is optional except for Other. "Machine
 * down" is live: confirming it marks the machine down too.
 */
export function CantModal({ task, open, onClose, onDone }: {
  task: TechTask;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  const act = useTechActions();
  const [reason, setReason] = useState<CantReason | null>(null);
  const [note, setNote] = useState('');
  const needsNote = reason === 'other' && !note.trim();
  const to = sendsTo(task);
  const send = () => {
    if (!reason) return;
    act.cant.mutate({ id: task.id, reason, note: note.trim() || undefined }, {
      onSuccess: () => {
        toast.success(`Sent back to ${to}`);
        onClose();
        onDone?.();
      },
      onError: (e) => toast.error(errorText(e)),
    });
  };
  return (
    <TechModal
      open={open}
      onClose={onClose}
      width={620}
      title={cantTitle(task)}
      sub="Pick one — the order goes back to the doctor with your line attached, never silently"
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn"
            style={{ background: 'var(--warning-600)', color: 'var(--surface-0)' }}
            disabled={!reason || needsNote || act.cant.isPending}
            onClick={send}
          >
            Send back to {to}
          </button>
        </>
      )}
    >
      <table className="dtable" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th style={{ width: '48%' }}>Reason</th>
            <th style={{ width: '52%' }}>What it does</th>
          </tr>
        </thead>
        <tbody>
          {CANT_REASONS.map((r) => (
            <tr
              key={r.key}
              className={`rowc${reason === r.key ? ' sel' : ''}`}
              onClick={() => setReason(r.key)}
              style={{ cursor: 'pointer' }}
              aria-selected={reason === r.key}
            >
              <td><span className={`t-body${reason === r.key ? ' w6' : ''}`}>{r.label}</span></td>
              <td>
                <span className="t-sec sec">{r.does}</span>
                {r.key === 'machine_down' && reason === 'machine_down' ? (
                  <p className="t-cap red mt4">
                    {task.device ? `${deviceLine(task.device)} — marked down when you send` : `No machine on file for ${task.kindLabel}`}
                  </p>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="card card-ghost mt12" style={{ cursor: 'text' }}>
        <p className="t-ovl mut">
          Note <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>(optional — required if you pick “Other”)</span>
        </p>
        <p className="t-sec sec mt4">
          <NoteField label="Note" value={note} onChange={setNote} maxLength={500}
            placeholder="what happened, in one line" />
        </p>
      </div>
    </TechModal>
  );
}
